import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { formatDateSafe } from '@/utils/dateUtils';
import { formatDayOffDates } from '@/features/DpRequests/utils/formatDayOffDates';
import { TONE_CLASSES, describeRequestStatus } from '@/features/DpRequests/dpRequests.definitions';
import {
    getCollaboratorProfileHref,
    getInitials,
    getProfileLinkLabel,
} from '@/features/Vacations/vacations.definitions';
import { DAY_OFF_DECIDED_LABELS, KIND_LABELS } from '../../approvals.definitions';
import type { DayOffDecidedRowProps } from './approvalDecidedList.types';

export function DayOffDecidedRow({ approval }: DayOffDecidedRowProps) {
    const { request } = approval;
    const status = describeRequestStatus(request);
    const decision = request.managerDecision;
    const decidedAt = decision?.at?.toDate?.() ?? null;
    const profileHref = request.collaboratorId ? getCollaboratorProfileHref(request.collaboratorId) : null;

    return (
        <li className="flex flex-wrap items-center gap-3 px-4 py-3">
            {profileHref ? (
                <Link
                    to={profileHref}
                    aria-label={getProfileLinkLabel(approval.collaboratorName)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-raro-gray-600 transition-colors hover:bg-primary-100 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 font-montserrat"
                >
                    {getInitials(approval.collaboratorName)}
                </Link>
            ) : (
                <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-raro-gray-600 font-montserrat"
                    aria-hidden="true"
                >
                    {getInitials(approval.collaboratorName)}
                </span>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-raro-gray-900 font-montserrat">
                    {approval.collaboratorName}
                </p>
                <p className="truncate text-xs text-raro-gray-500 font-montserrat">
                    {KIND_LABELS.day_off} · {formatDayOffDates(request.days).join(' · ')}
                </p>
                {decision?.note && (
                    <p className="mt-1 text-xs text-raro-gray-600 font-montserrat">
                        <span className="font-medium">{DAY_OFF_DECIDED_LABELS.note}:</span> {decision.note}
                    </p>
                )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
                <span
                    className={clsx(
                        'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset font-montserrat',
                        TONE_CLASSES[status.tone]
                    )}
                >
                    {status.label}
                </span>
                {decidedAt && (
                    <span className="text-[11px] text-raro-gray-400 font-montserrat">
                        {DAY_OFF_DECIDED_LABELS.decidedAt} {formatDateSafe(decidedAt)}
                    </span>
                )}
            </div>
        </li>
    );
}
